import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import WebGLVectorLayer from 'ol/layer/WebGLVector.js';
import VectorSource from 'ol/source/Vector.js';
import { unByKey } from 'ol/Observable';
import { LuFileDown } from 'react-icons/lu';
import { Spinner } from 'react-bootstrap';
import { LuLayoutGrid } from 'react-icons/lu';

import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { getRotatedGrid, getDSP, getDSPZipFile, getFootprintGeoJson } from '@utils/api';
import { deleteLayerByCustomId, getLayerByCustomId } from '@utils/map/helpers';
import { createTooltip } from '@utils/map/tooltip.overlay';

import LayerSettings from '../../sidebars/sidebarElements/LayerSettings';
import {
	ToggleSlideButton,
	ToggleSlideContent,
} from '@components/toggleSlideContent/ToggleSlideContent';
import { FootprintTooltip } from './OverlayTooltips';
import DspDetailsPanel from './DspDetailsPanel';
import DspDetectionsPerPlotSidebar, {
	DSP_DETECTIONS_SIDEBAR_ID,
} from '../../sidebars/DspDetectionsPerPlotSidebar';
import { extractPerClassPerPlot } from './DspDetailsPanel';

import EstimateWarning from './EstimateWarning';
import { downloadFile } from '@utils/files';

import { parseFootprintData, parseDspData, handleFootprintFeatureHover } from './helpers';

const defaultVisible = false;
export const defaultStyle = {
	'fill-color': [
		'case',
		['>', ['get', 'hovering'], 0], // Changed from == true to > 0
		'rgba(0, 0, 0, 0.5)',
		'rgba(0, 0, 0, 0.3)',
	],
	'stroke-color': [
		'case',
		['>', ['get', 'hovering'], 0], // Changed from == true to > 0
		'rgba(255, 255, 0, 0.8)',
		'rgba(0, 0, 0, 0.8)',
	],
	'stroke-width': 2,
};

//const isDevMode = process.env.NODE_ENV === 'development';

const SingleImageFootprintsLayer = ({
	layerId = 'singleImageFootprintsLayer',
	layerName = 'Footprints',
	showDsp = false,
	setDspColorFilterActive,
}) => {
	const featureType = showDsp ? 'digitalSamplePlot' : 'footprint';

	const {
		mapObject,
		project,
		pickedTask,
		colorScheme,
		toolBarVisible,
		pickedModelClasses,
		dspFeaturesInfo,
		singleImageFeatures,
		interactionsDisabled,
		dispatch,
		removeFromActiveSidebars,
	} = useProject();
	const { addToast } = useToast();
	const { uuid: projectUuid } = project ?? {};
	const { model_uuid: modelUuid } = pickedTask ?? {};

	const adding = useRef(false);
	const removeFromActiveSidebarsRef = useRef(removeFromActiveSidebars);

	const hoveredFeature = useRef(null);
	const tooltipContainerRef = useRef(null);
	const [tooltipContent, setTooltipContent] = useState(null);
	const [showLayerDetails, setShowLayerDetails] = useState(false);

	const interactionsDisabledRef = useRef(interactionsDisabled);
	interactionsDisabledRef.current = interactionsDisabled;

	const singleImageFeaturesRef = useRef(singleImageFeatures);
	singleImageFeaturesRef.current = singleImageFeatures;

	const [singleImageFootprintsLayer, setSingleImageFootprintsLayer] = useState(null);
	const [features, setFeatures] = useState([]);

	const { data } = useQuery({
		queryKey: ['fetchRotatedGrid', projectUuid, modelUuid],
		queryFn: () => getRotatedGrid(projectUuid),
		enabled: !!project?.uuid && !!mapObject && !showDsp,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const { data: dspData } = useQuery({
		queryKey: ['fetchDsp', projectUuid, modelUuid],
		queryFn: () => getDSP(projectUuid, modelUuid),
		enabled:
			!!project?.uuid && !!mapObject && showDsp && modelUuid && !!pickedModelClasses?.length,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const { refetch: fetchDspZip, isFetching: isFetchingDspZip } = useQuery({
		queryKey: ['fetchDspZip', projectUuid, modelUuid],
		queryFn: () => getDSPZipFile(projectUuid, modelUuid),
		enabled: false,
		gcTime: 0,
	});

	const { refetch: fetchFootprintGeoJson, isFetching: isFetchingFootprintGeoJson } = useQuery({
		queryKey: ['fetchFootprintGeoJson', projectUuid],
		queryFn: () => getFootprintGeoJson(projectUuid),
		enabled: false,
		gcTime: 0,
	});

	const downloadDspOrFootprintsFile = async () => {
		if (showDsp) {
			if (!projectUuid || !modelUuid) return;
			const { data: blob, isError } = await fetchDspZip();
			if (isError) {
				addToast({
					id: 'dsp-download-error',
					bg: 'danger',
					title: 'Failed to download DSP file',
				});
				return;
			}
			if (!blob || blob.size === 0) return;
			const url = URL.createObjectURL(blob);
			downloadFile({
				url,
				title: `${project?.title}_DSP`,
				fileExtension: 'zip',
			});
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		} else {
			if (!projectUuid) return;
			const { data: blob, isError } = await fetchFootprintGeoJson();
			if (isError) {
				addToast({
					id: 'footprint-download-error',
					bg: 'danger',
					title: 'Failed to download Footprints file',
				});
				return;
			}
			if (!blob) return;
			const url = URL.createObjectURL(
				new Blob([JSON.stringify(blob)], {
					type: 'application/geo+json',
				})
			);
			downloadFile({
				url,
				title: `${project?.title}_Footprints`,
				fileExtension: 'geojson',
			});
			setTimeout(() => URL.revokeObjectURL(url), 1000);
		}
	};

	useEffect(() => {
		if (showDsp || !data?.grid_geojson) return;

		const parsedFeatures = parseFootprintData({ data: data.grid_geojson, featureType });
		if (parsedFeatures) {
			setFeatures(parsedFeatures);
		}

		return () => {
			setFeatures([]);
		};
	}, [data, showDsp]);

	useEffect(() => {
		if (!showDsp || !dspData || !dspFeaturesInfo) return;

		const parsedFeatures = parseDspData({
			data: dspData,
			featureType,
			dspFeaturesInfo,
			pickedModelClasses,
		});
		if (parsedFeatures) {
			setFeatures(parsedFeatures);
		}

		return () => {
			setFeatures([]);
		};
	}, [showDsp, dspData, pickedModelClasses, dspFeaturesInfo]);

	const collectDspFeaturesInfo = useCallback(() => {
		if (!data?.grid_geojson) return;

		// The endpoint for getting dsp data does not have alle the info we need
		// so we need to get the features from the rotated grid endpoint

		const featuresData = data?.grid_geojson?.features || [];

		const items = featuresData.map(feature => {
			const coordinates = feature.geometry?.coordinates;
			if (!coordinates) {
				return null;
			}

			const { properties } = feature || {};

			const _lrfActive = properties.image_location?.lrf_active;

			return {
				tile_name: properties.tile_name,
				lrfActive: _lrfActive,
			};
		});

		const filteredItems = items.filter(item => item);
		dispatch({
			type: 'setDspFeaturesInfo',
			payload: filteredItems,
		});
	}, [data]);

	useEffect(() => {
		collectDspFeaturesInfo();
	}, [collectDspFeaturesInfo]);

	const hasRoughEstimate = useMemo(() => {
		if (!dspFeaturesInfo?.length) return false;
		return dspFeaturesInfo.some(item => item.lrfActive === false);
	}, [dspFeaturesInfo]);

	const handleFeatureHoverCallback = useCallback(
		(feature, tooltip) => {
			handleFootprintFeatureHover({
				feature,
				tooltip,
				mapObject,
				toolBarVisible,
				featureType,
				hoveredFeature,
				setTooltipContent,
			});
		},
		[mapObject, toolBarVisible, featureType, dspData, data]
	);

	const updateFootprintLayer = useCallback(() => {
		if (mapObject && !adding.current) {
			adding.current = true;

			const existingLayer = getLayerByCustomId(mapObject, layerId);

			if (!features?.length) {
				adding.current = false;

				if (existingLayer) {
					deleteLayerByCustomId(mapObject, layerId);
				}

				return;
			}

			const footprintVectorSource = new VectorSource({
				features: features,
			});

			let currentLayer = null;

			if (existingLayer) {
				currentLayer = existingLayer;
				currentLayer.setSource(footprintVectorSource);
			} else {
				currentLayer = new WebGLVectorLayer({
					source: footprintVectorSource,
					zIndex: 11,
					name: layerName,
					properties: {
						customLayerId: layerId,
					},
					style: defaultStyle,
					visible: defaultVisible,
				});

				mapObject.addLayer(currentLayer);
				console.log(`Added ${layerId} layer`);

				// Expose layer to context only on first creation (not on source updates)
				dispatch({ type: 'setDspFootprintsLayer', payload: showDsp ? currentLayer : null });
			}

			setSingleImageFootprintsLayer(currentLayer);
			adding.current = false;
		}
	}, [features]);

	useEffect(() => {
		updateFootprintLayer();
	}, [updateFootprintLayer]);

	// Keep ref updated with latest function
	// (removeFromActiveSidebars is not memoized in context, so we use a ref to avoid
	// effect re-runs while still accessing the latest reference on unmount)
	useEffect(() => {
		removeFromActiveSidebarsRef.current = removeFromActiveSidebars;
	});

	// Clear context reference and sidebar when this component unmounts (e.g., navigating away)
	useEffect(() => {
		return () => {
			dispatch({ type: 'setDspFootprintsLayer', payload: null });
			removeFromActiveSidebarsRef.current(DSP_DETECTIONS_SIDEBAR_ID);
		};
	}, [dispatch]);

	// Open associated image when a DSP polygon is clicked
	useEffect(() => {
		if (!mapObject || !singleImageFootprintsLayer || !showDsp) return;

		const handleDspClick = event => {
			if (interactionsDisabledRef.current) return;
			if (!singleImageFootprintsLayer.getVisible()) return;

			const feature = mapObject.forEachFeatureAtPixel(event.pixel, f => f, {
				layerFilter: layer => layer === singleImageFootprintsLayer,
			});
			if (!feature) return;

			const filename = feature.get('filename');
			if (!filename) return;

			const imageFeature = singleImageFeaturesRef.current?.find(
				img => img.get('name') === filename
			);

			if (!imageFeature) {
				console.warn(`DSP click: no image feature found for "${filename}"`);
				return;
			}

			dispatch({ type: 'setSelectedSingleImage', payload: imageFeature });
		};

		const key = mapObject.on('singleclick', handleDspClick);

		return () => {
			unByKey(key);
		};
	}, [mapObject, singleImageFootprintsLayer, showDsp]);

	useMapTooltip({
		mapObject,
		tooltipContainerRef,
		layerId,
		onFeatureHover: handleFeatureHoverCallback,
	});

	const perClassPerPlot = useMemo(() => extractPerClassPerPlot(dspData), [dspData]);

	if (!singleImageFootprintsLayer) return null;

	return (
		<li>
			<div className="d-flex flex-row justify-content-between align-items-center">
				<div className="d-flex flex-row gap-2 align-items-center">
					<LayerSettings
						id={layerId}
						layer={singleImageFootprintsLayer}
						label={singleImageFootprintsLayer.get('name')}
						defaultState={singleImageFootprintsLayer?.getVisible()}
						onCheck={() => {
							singleImageFootprintsLayer?.setVisible(true);
						}}
						onUncheck={() => {
							singleImageFootprintsLayer?.setVisible(false);
						}}
						disabled={!singleImageFootprintsLayer}
						layerFunctions={[
							...(showDsp
								? [
										{
											title: 'Color Filter',
											icon: <LuLayoutGrid />,
											onClick: setDspColorFilterActive,
										},
									]
								: []),
							{
								title: showDsp ? 'Export ZIP' : 'Export GeoJSON',
								icon:
									(showDsp && isFetchingDspZip) ||
									(!showDsp && isFetchingFootprintGeoJson) ? (
										<Spinner size="sm" />
									) : (
										<LuFileDown />
									),
								onClick: downloadDspOrFootprintsFile,
							},
						]}
						layerExplanations={
							hasRoughEstimate ? [<EstimateWarning key="estimate" />] : []
						}
					/>
				</div>
				{showDsp && (
					<div className="position-relative me-2">
						<div className="position-absolute top-0 start-100 translate-middle">
							<ToggleSlideButton
								constrolsId="dsp-layer-details"
								isOpen={showLayerDetails}
								setIsOpen={setShowLayerDetails}
							/>
						</div>
					</div>
				)}
			</div>

			{showDsp && (
				<ToggleSlideContent isOpen={showLayerDetails} id="dsp-layer-details">
					<DspDetailsPanel
						dspFeatures={features}
						mapObject={mapObject}
						isOpen={showLayerDetails}
						perClassPerPlot={perClassPerPlot}
					/>
				</ToggleSlideContent>
			)}

			<div ref={tooltipContainerRef} className="ol-tooltip">
				<FootprintTooltip {...tooltipContent} colorScheme={colorScheme} />
			</div>

			{showDsp && <DspDetectionsPerPlotSidebar perClassPerPlot={perClassPerPlot} />}
		</li>
	);
};
export default SingleImageFootprintsLayer;

const useMapTooltip = ({ mapObject, tooltipContainerRef, layerId, onFeatureHover }) => {
	const tooltipRef = useRef(null);
	const pointermoveKeyRef = useRef(null);

	useEffect(() => {
		// Clean up previous listener if it exists
		if (pointermoveKeyRef.current) {
			unByKey(pointermoveKeyRef.current);
			pointermoveKeyRef.current = null;
		}

		// Return early if required dependencies aren't ready
		if (!mapObject || !tooltipContainerRef.current) return;

		// Initialize tooltip if not already created
		if (!tooltipRef.current) {
			tooltipRef.current = createTooltip({
				mapRef: mapObject,
				tooltipRef: tooltipContainerRef.current,
				id: `image-footprint-tooltip-${layerId}`,
			});
		}

		// Set up pointer move handler
		const handlePointerMove = event => {
			if (!mapObject || !tooltipRef.current) return;

			const feature = mapObject.forEachFeatureAtPixel(event.pixel, feature => feature);

			onFeatureHover(feature, tooltipRef.current);
		};

		// Attach event listener
		pointermoveKeyRef.current = mapObject.on('pointermove', handlePointerMove);

		return () => {
			if (pointermoveKeyRef.current) {
				unByKey(pointermoveKeyRef.current);
				pointermoveKeyRef.current = null;
			}
		};
	}, [mapObject, tooltipContainerRef.current, layerId, onFeatureHover]); // Added .current to tooltipContainerRef

	return tooltipRef;
};
